import { Box, List, ListItem, ListItemText, Skeleton, Stack, Typography } from '@mui/material';
import { RichTreeView } from '@mui/x-tree-view';
import HomeIcon from '@mui/icons-material/Home';
import React, { useMemo, useState } from 'react';
import { Folder } from '../../types';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { CreateFolderButton } from '../CreateFolderButton/CreateFolderButton';
import { FoldersListItemButton } from './FoldersListItemButton';
import { styles } from './FoldersList.styles';
import { FoldersListSearchInput } from './FoldersListSearchInput';
import { FoldersListSortingSelect } from './FoldersListSortingSelect';
import times from 'lodash/times';
import { FoldersListTreeItem } from './FoldersListTreeItem';
import { convertFoldersToTreeViewItems, getTreeViewItemsForSearch } from './FoldersList.utils';
import { useFolderListExpandedItems } from './hooks/useFolderListExpandedItems/useFolderListExpandedItems';

const LOADING_FOLDER_SKELETON_COUNT = 3;
const intl = {
  foldersHeader: 'Folders',
  noMatchingFoldersMessage: 'No matching folders found'
};

type FoldersListProps = {
  expandedItemsKey: string;
  isFoldersLoading?: boolean;
  onFolderClick?: (folderId: Folder['id']) => void;
};

export function FoldersList({ expandedItemsKey, isFoldersLoading = false, onFolderClick }: FoldersListProps) {
  const {
    foldersMap,
    activeFolder,
    setActiveFolder,
    rootFolder,
    folderAbilities: { create: canCreate },
    foldersSorting,
    onFoldersSortingChange
  } = useFoldersContext();

  const [searchValue, setSearchValue] = useState('');
  const subfolders = useMemo(() => rootFolder.subfolders || [], [rootFolder.subfolders]);
  const treeViewItems = useMemo(() => {
    if (searchValue) {
      const treeViewItemsForSearch = getTreeViewItemsForSearch(subfolders);
      return treeViewItemsForSearch.filter((treeViewItem) =>
        treeViewItem.label.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    return convertFoldersToTreeViewItems(subfolders);
  }, [searchValue, subfolders]);

  const { expandedItems, handleItemExpansionToggle } = useFolderListExpandedItems({
    expandedItemsKey,
    activeFolderId: activeFolder.id && activeFolder.id !== rootFolder.id ? activeFolder.id : null,
    folders: subfolders,
    foldersMap
  });

  const handleFolderClick = (folder: Folder) => {
    setActiveFolder(folder);
    if (onFolderClick) {
      onFolderClick(folder.id);
    }
    setSearchValue('');
  };
  const handleFolderSelection = (itemId: string) => {
    const folder = foldersMap.get(itemId);
    if (folder) {
      handleFolderClick(folder);
    }
  };
  const handleSelectedFolderChange = (event: React.SyntheticEvent, itemId: string | null) => {
    if (event.nativeEvent instanceof KeyboardEvent && event.nativeEvent.key === 'Enter' && itemId) {
      handleFolderSelection(itemId);
    }
  };

  return (
    <>
      <List sx={styles.listHeader} data-test="foldersListHeader">
        <FoldersListItemButton
          selected={activeFolder.id === rootFolder.id}
          icon={<HomeIcon />}
          text={rootFolder.title}
          component="h1"
          onClick={() => {
            handleFolderClick(rootFolder);
          }}
        />

        <ListItem sx={styles.headerContainer} component="li">
          <ListItemText
            primary={intl.foldersHeader}
            primaryTypographyProps={{
              sx: styles.header,
              component: 'h2'
            }}
          />
          {canCreate ? <CreateFolderButton /> : null}
        </ListItem>
      </List>

      {subfolders.length > 0 ? (
        <Box sx={styles.searchAndSortingContainer}>
          <FoldersListSearchInput value={searchValue} disabled={isFoldersLoading} onChange={setSearchValue} />
          <FoldersListSortingSelect
            value={foldersSorting}
            disabled={isFoldersLoading}
            onChange={(foldersSorting) => {
              if (onFoldersSortingChange) {
                onFoldersSortingChange(foldersSorting);
              }
            }}
          />
        </Box>
      ) : null}

      {isFoldersLoading ? (
        <Stack sx={styles.skeletonContainer} data-test="foldersSkeleton">
          {times(LOADING_FOLDER_SKELETON_COUNT).map((_, index) => (
            <Skeleton key={index} variant="rectangular" sx={styles.folderSkeleton} />
          ))}
        </Stack>
      ) : null}

      {treeViewItems.length > 0 ? (
        <Box sx={styles.list} data-test="foldersList">
          <RichTreeView
            multiSelect={false}
            aria-label="folders tree"
            items={treeViewItems}
            expansionTrigger="iconContainer"
            disabledItemsFocusable={true}
            onItemClick={(_, folderId) => {
              handleFolderSelection(folderId);
            }}
            expandedItems={expandedItems}
            onItemExpansionToggle={(event, folderId, isExpanded) => {
              const newExpandedItems = isExpanded
                ? [...expandedItems, folderId]
                : expandedItems.filter((id) => id !== folderId);
              handleItemExpansionToggle(newExpandedItems);
              handleSelectedFolderChange(event, folderId);
            }}
            onSelectedItemsChange={handleSelectedFolderChange}
            slots={{ item: FoldersListTreeItem }}
          />
        </Box>
      ) : null}

      {subfolders.length > 0 && treeViewItems.length === 0 ? (
        <Typography variant="bodySmall" component="div" sx={styles.noMatchingFolders}>
          {intl.noMatchingFoldersMessage}
        </Typography>
      ) : null}
    </>
  );
}
